/**
 * THE DOCK (I-03 R6 · S3-06.3, proposal §3e/§4).
 *
 * Operator ruling 2026-09-10: "FroggerAI should be the way you get
 * around it… have your phone or tablet out and be talking to the app."
 * So it is not a page. It is a dock on EVERY facility page, the page it
 * is on IS its current place, and it navigates.
 *
 * ── TWO LAYOUTS, ONE CONVERSATION ─────────────────────────────────────
 *
 * Desktop (>= 1024): a 380px right-hand panel, toggled by the navbar
 * button or the backtick key, remembered for the session.
 *
 * Phone / tablet (< 1024): a bottom dock. COLLAPSED it is one row —
 * place chip, composer, mic, send — because a keeper standing at a rack
 * wants to say one thing, not read a transcript. EXPANDED it is a bottom
 * sheet with the transcript above that same row.
 *
 * The chat is MOUNTED ONCE and CSS decides whether its log is on screen.
 * Unmounting it to collapse would throw away the transcript, and the
 * transcript is the only record of the round the keeper is halfway
 * through.
 *
 * ── IT SITS ABOVE THE PHONE BAR, MEASURED, NOT ASSUMED ────────────────
 *
 * The racks `PhoneBar` is STICKY AT THE TOP, so on the racks page the
 * offset is correctly zero — a blind `56px` lift would have parked the
 * dock off the floor for no reason. What is measured is any bar that is
 * actually ANCHORED TO THE BOTTOM of the viewport, and the dock rides
 * above it. And the dock is never INSIDE a sheet (the S3-03.2 rule): it
 * is mounted in App.js as a sibling of <main>, so no sheet's unmount can
 * take it with it.
 *
 * ── IT RESERVES ITS OWN SPACE ─────────────────────────────────────────
 *
 * Below 1024 the dock is `position: fixed` at the bottom, which takes it
 * out of flow — so the last ~100px of EVERY facility page sat underneath
 * it: the footer, the bottom of a long tank list, the last row of a
 * table. A permanent fixture that eats the end of every page is not a
 * dock, it is an obstacle. It publishes its measured height as
 * `--ff-dock-height` on `<html>` and `.app-content` pads by it, and it
 * sets that variable back to `0px` the moment it hides or unmounts —
 * because a stale reserve is the same bug upside down.
 *
 * Not spoken. No TTS — the read-back is one line on screen (ruling).
 * Vocabulary: enclosure / tank / rack / room. Never the other word.
 */

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import FroggerAIChatPanel from './FroggerAIChatPanel';
import useFroggerNavigate from './useFroggerNavigate';
import { usePlace, placeLabel, placeChipParts } from '../../context/PlaceContext';
import { useSignedInUserId } from '../../context/AuthContext';
import { froggerKeys } from '../../utils/froggerStorageKeys';
import './FroggerDock.css';
import { measureTopBar, topBarNodes } from '../../utils/topBar';

/**
 * THE COLLAPSED PREVIEW LINE — batch 8 item A.
 *
 * The answer's FIRST LINE, on one line, ellipsized. A cap here as well
 * as in CSS because the string is also the button's `title` and an
 * `aria-label`, and a whole paragraph read out by a screen reader is
 * not a preview either.
 */
export const PREVIEW_MAX = 140;

/**
 * The answer's Markdown EMPHASIS taken off, for a line that is not
 * rendered as Markdown (Cowork run 24, closure item 7). The transcript
 * draws `**bold**` bold (finding 8); the collapsed preview is a plain
 * string — also its `title` and its `aria-label` — so the markers were
 * printed as asterisks and READ OUT by a screen reader. Bold and italics,
 * `*`/`_` in pairs only, so a lone asterisk or a `snake_case` name is
 * left as it is. No lookbehind: this ships to phones.
 */
export const stripEmphasis = (text) => String(text || '')
  .replace(/(\*\*|__)(?=\S)([^\n]*?\S)\1/g, '$2')
  .replace(/\*(?=\S)([^*\n]*?\S)\*/g, '$1')
  .replace(/(^|[^A-Za-z0-9_])_(?=\S)([^_\n]*?\S)_(?![A-Za-z0-9_])/g, '$1$2');

/** The one line a collapsed dock shows for an answer. */
export const previewLine = (text) => {
  const first = String(text || '').split('\n').map((s) => s.trim())
    .find((s) => s.length > 0) || '';
  const plain = stripEmphasis(first);
  return plain.length > PREVIEW_MAX
    ? `${plain.slice(0, PREVIEW_MAX - 1)}…` : plain;
};

/** Below this the dock is a bottom sheet; at or above it, a side panel. */
export const DOCK_DESKTOP_MIN = 1024;

/** The dock's open state, remembered for the SESSION only — and per
 * signed-in user (`froggerKeys.dockOpen`, R-TRAINING junior fix 1). */

/** The event a navbar button (or anything else) fires to toggle it. */
export const TOGGLE_EVENT = 'froggerai:toggle-dock';

/**
 * The event another page fires to hand the dock a turn — Tasks Today's
 * "Walk me through it" is the first caller.
 * `CustomEvent('froggerai:ask', {detail: {utterance, place?}})`.
 */
export const ASK_EVENT = 'froggerai:ask';

const readOpen = (userId) => {
  try { return sessionStorage.getItem(froggerKeys.dockOpen(userId)) === '1'; } catch (e) { return false; }
};

/**
 * A keystroke that belongs to something the operator is TYPING IN is not
 * a shortcut. Without this, a backtick in a tank note or a search box
 * opened the dock and swallowed the character.
 */
export const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toUpperCase();
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (el.isContentEditable) return true;
  return false;
};

/**
 * How far off the floor the dock has to sit.
 *
 * ONLY a bar anchored to the BOTTOM of the viewport counts. A sticky top
 * bar (which is what the racks `PhoneBar` is) contributes nothing, and a
 * bar that is scrolled off screen contributes nothing either.
 */
export const measureBottomBar = (doc = document, win = window) => {
  let offset = 0;
  let nodes = [];
  try {
    nodes = Array.from(doc.querySelectorAll(
      '[data-phone-bar="1"], [data-froggerai-bottom-bar="1"]'));
  } catch (e) { return 0; }
  nodes.forEach((el) => {
    const rect = el.getBoundingClientRect?.();
    if (!rect || !rect.height) return;
    if (rect.bottom >= (win.innerHeight || 0) - 2) {
      offset = Math.max(offset, Math.round(rect.height));
    }
  });
  return offset;
};

/**
 * HOW FAR DOWN THE DESKTOP PANEL HAS TO START — MEASURED, like the floor.
 *
 * ⚠️ `--ff-navbar-height` IS NOT DEFINED ANYWHERE IN THIS APP. Two files
 * read it and each invented its own fallback (`FroggerDock.css` 64px,
 * `styles/tasks.css` 60px); the real navbar is **97px** at 1440 and it is
 * responsive, so no constant is right for long. The dock's head — the
 * place chip and the collapse toggle — therefore started 33px UNDER a
 * `fixed-top` navbar that layers above it (1050 vs 1040), and
 * `elementFromPoint` at the chip's own centre returned the navbar.
 * Found by the S3-06.3c harness, which asks the browser what is at each
 * control rather than trusting a token.
 *
 * The CSS keeps its fallback for the frame before this runs; anything
 * measured wins. Same doctrine as `measureBottomBar` — only a bar
 * actually anchored to the TOP of the viewport counts, so a sticky bar
 * scrolled out of the way contributes nothing.
 */
// The top-bar measurement lives in `utils/topBar` since fix batch 33 (R11,
// 2026-10-07): the navbar's own spacer reads the SAME measurement rather
// than a second constant. Re-exported here for its existing callers.
export { TOP_BAR_SELECTOR, topBarNodes, measureTopBar } from '../../utils/topBar';

/**
 * WHERE THE PANEL STARTS WHEN THE BAR CANNOT BE MEASURED.
 *
 * Cowork QA of R6 (2026-09-11) found the place chip UNDER the navbar at
 * 1440 and at 1194 landscape: the navbar is 97px and the panel's `top`
 * was 64px. 64 was the CSS fallback — `--ff-navbar-height` is defined
 * nowhere — and it was reached because the inline style was only applied
 * `topOffset ? {...} : undefined`, so any frame in which the measurement
 * came back 0 rendered at 64 and stayed there.
 *
 * Two changes, and both matter:
 *
 *  - the fallback is 97, the height the bar actually is. A fallback that
 *    is too SHORT hides a control under a bar that layers above it
 *    (navbar 1050 vs dock 1040); too tall costs a few pixels.
 *  - `dockTop()` ALWAYS returns a number, so the inline `top` is always
 *    present and the CSS fallback is only ever the very first frame.
 *
 * And the measurement itself now runs in a LAYOUT effect (before paint)
 * as well as after it, so the correct value is in place on the first
 * painted frame rather than one frame late.
 */
export const DEFAULT_TOP_BAR = 97;

/**
 * Does this device put a KEYBOARD on the screen when a field has focus?
 *
 * A coarse pointer is the honest proxy — it is what `useIsPhone` uses for
 * the other half of the same question — and it is deliberately NOT a
 * width: an iPad in landscape is 1194px wide and still types on glass.
 * Safe anywhere (`matchMedia` is missing in jsdom without a polyfill).
 */
export const hasSoftKeyboard = (win = (typeof window === 'undefined' ? null : window)) => {
  if (!win || typeof win.matchMedia !== 'function') return false;
  try {
    return !!win.matchMedia('(pointer: coarse)').matches;
  } catch (e) { return false; }
};

/** The panel's `top`: measured when we have it, 97 when we do not. */
export const dockTop = (measured) => (
  Number.isFinite(measured) && measured > 0 ? measured : DEFAULT_TOP_BAR);

export default function FroggerDock({ facilityId, facilityName }) {
  const { place, nowhere, clearPlace } = usePlace();
  const navigateTo = useFroggerNavigate(facilityId);
  const { pathname } = useLocation();
  const userId = useSignedInUserId();

  const [open, setOpen] = useState(() => readOpen(userId));
  const [isDesktop, setIsDesktop] = useState(
    () => (typeof window === 'undefined'
      ? true : window.innerWidth >= DOCK_DESKTOP_MIN));
  const [bottomOffset, setBottomOffset] = useState(0);
  const [topOffset, setTopOffset] = useState(0);
  // `{text, seq}` and not a bare string: two identical asks are two
  // turns, and the seq is what says so (see the panel's `utteranceKey`).
  const [pending, setPending] = useState({ text: '', seq: 0 });
  // The chat is mounted from the moment the dock is first opened and
  // stays mounted: closing the panel must not throw away the round the
  // keeper is halfway through. On a phone the collapsed row IS the dock,
  // so it is mounted from the start.
  const [everOpened, setEverOpened] = useState(() => readOpen(userId));

  // ── A (batch 8): THE ANSWER HAS TO BE VISIBLE ──────────────────────
  //
  // Operator, phone eye test 2026-09-12: the collapsed one-row dock said
  // "Thinking", the reply landed in the transcript the row is hiding,
  // and the only way to it was the caret — *"you have to pop it"*. A
  // keeper who asked a question is owed the answer without a second tap.
  //
  // Two halves, and they are different promises:
  //  - the dock EXPANDS ITSELF the moment a turn produces anything (the
  //    first token, a read-back, the round's counts), so the answer
  //    arrives on screen rather than behind a control; and
  //  - a COLLAPSED dock still shows the last answer's first line as a
  //    preview, with an unread dot until it is opened — because the
  //    navigate rule collapses the dock ON PURPOSE (the keeper asked to
  //    SEE something) and the read-back, with its Undo, must not go
  //    with it.
  //
  // PHONE LAYOUT ONLY, like the navigate collapse: the desktop panel is
  // a column beside the page and its transcript is already on screen.
  const [preview, setPreview] = useState('');
  const [unread, setUnread] = useState(false);
  // `open` read at CALLBACK time, not closed over: the panel's chunk
  // handler fires from a stream reader and the state it captured may be
  // a render old.
  const openRef = useRef(false);
  openRef.current = open;

  const toggle = useCallback((next) => {
    setOpen((prev) => {
      const value = typeof next === 'boolean' ? next : !prev;
      try { sessionStorage.setItem(froggerKeys.dockOpen(userId), value ? '1' : '0'); } catch (e) { /* private */ }
      if (value) setEverOpened(true);
      return value;
    });
  }, [userId]);

  // Opening it IS reading it.
  useEffect(() => { if (open) setUnread(false); }, [open]);

  // ── A NAVIGATE GETS OUT OF THE WAY (S3-06.4) ───────────────────────
  //
  // Expanded, the bottom dock is 659px of an 844px phone — and 726 of
  // 1194 on an iPad in portrait. "Open Bruce" then landed on the row,
  // scrolled to it, and painted the transcript on top of it:
  // `elementFromPoint` at the row's own centre returned the dock. The
  // keeper asked to SEE something, so the dock stands down to its one
  // row and the page is the thing on screen.
  //
  // PHONE LAYOUT ONLY. The desktop panel is a column BESIDE the page and
  // covers nothing, and collapsing it there would hide a conversation
  // the keeper can perfectly well read next to what they asked for.
  //
  // The collapse rides with the navigation rather than after it: the
  // landing's own `scrollIntoView` is what puts the row on screen, and
  // `html { scroll-padding-bottom: var(--ff-dock-height) }` is what
  // keeps it clear of whatever height the dock has by then. Over-
  // reserving (the frame before the collapse lands) is safe; under-
  // reserving is the bug.
  // The dock's own box: measured for the reserve below, and read by
  // `onNavigate` to tell the dock's own focus from the page's.
  // ⚠️ DECLARED ABOVE ITS READERS, not next to the effect that measures
  // it — a `const` a hook closes over from further up the component is
  // a TDZ the minifier is happy to expose (the S3-01 lesson).
  const rootRef = useRef(null);

  // ⚠️ AND THE KEYBOARD GOES WITH IT — ON A DIFFERENT TEST FROM THE
  // COLLAPSE, because they are answers to two different questions.
  //
  // The COLLAPSE is about the viewport: a 380px column beside the page
  // hides nothing, a sheet across the bottom hides the row. The BLUR is
  // about the INPUT DEVICE: the composer still holds focus when the turn
  // lands, so a device with a soft keyboard keeps another ~300px of
  // keyboard over the bottom of the very thing the keeper asked to see.
  // An iPad in LANDSCAPE is both at once — 1194px wide, so it gets the
  // desktop panel, and a touch screen, so it gets the keyboard — which
  // is exactly the case a layout-only test got wrong. Same distinction
  // the racks board draws between `DOCK_DESKTOP_MIN` (width) and
  // `useIsPhone` (pointer); a mouse-driven window keeps its focus and
  // can go on typing.
  //
  // The blur is also what stops the WEBKIT HOST CRASH the S3-06.3c
  // harness hit on this exact landing, on every touch descriptor. The
  // engine's UI process schedules a content-relative-views update for the
  // FOCUSED TEXT FIELD; when the hysteresis timer fires after the
  // landing's programmatic scroll it calls
  // `-[NSTextInputContext textInputClientDidUpdateSelection]`, a selector
  // macOS 15.0.1 does not have, and the whole browser aborts — no JS
  // error, no `crash` event, just a dead target (Playwright 1.63 /
  // webkit-2359). Nothing in our JS causes that and nothing in our JS can
  // repair it; with no focused field there is no input context to update,
  // so the path is never taken. Written for the keyboard, verified
  // against the crash.
  // A — the turn produced SOMETHING. On the bottom layout the dock puts
  // itself up so the keeper reads the answer instead of hunting for it.
  // Fired at most once per turn by the panel, on the FIRST sign of an
  // answer — never at completion, because the navigate collapse below
  // runs on a timer after `done` and an expand at completion would race
  // it and win.
  const onAnswer = useCallback(() => {
    if (isDesktop) return;
    toggle(true);
  }, [isDesktop, toggle]);

  // …and the line a collapsed dock shows. The panel sends the read-back
  // when there is one and the answer's own text otherwise, so the
  // receipt for a write is what survives a navigate collapse.
  // ⚠️ `quiet` (review of batch 8, MAJOR 1). A pre-routed sentence —
  // "Stopped.", "Okay — nothing created.", the next card, an Undo that
  // landed — still gets its preview line, because a collapsed dock has
  // nowhere else to say it. What it does NOT get is the unread dot: the
  // keeper is looking at the screen they just spoke to, and a dot is a
  // claim that something is waiting for them.
  const onPreview = useCallback((text, { quiet } = {}) => {
    const line = previewLine(text);
    if (!line) return;
    setPreview(line);
    if (!openRef.current && !quiet) setUnread(true);
  }, []);

  // MINOR 3 — A NEW CONVERSATION HAS NO LAST ANSWER. The preview line
  // and its dot describe a turn from a conversation that has just been
  // thrown away; leaving them up is the dock pointing at a transcript
  // the button has already cleared.
  const onNewConversation = useCallback(() => {
    setPreview('');
    setUnread(false);
  }, []);

  const onNavigate = useCallback((to) => {
    // The keeper asked to SEE something and the dock is getting out of
    // the way — so the answer it is taking with it is marked unread and
    // left on the preview line, Undo and all, one tap away.
    if (!isDesktop) { toggle(false); setUnread(true); }
    if (hasSoftKeyboard()) {
      const active = document.activeElement;
      if (active && typeof active.blur === 'function'
          && active !== document.body
          && rootRef.current && rootRef.current.contains(active)) {
        active.blur();
      }
    }
    navigateTo(to);
  }, [isDesktop, navigateTo, toggle]);

  // --- layout -------------------------------------------------------
  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= DOCK_DESKTOP_MIN);
    onResize();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  // ⚠️ BEFORE PAINT. The effect below runs its first measurement inside
  // `requestAnimationFrame`, which fires AFTER the browser has painted —
  // so the first painted frame used the CSS fallback, and if anything
  // later short-circuited the state update it stayed there. This runs
  // synchronously after the DOM is in place and before the paint, which
  // is exactly what a layout effect is for. It is cheap (one
  // `querySelectorAll` and a `getBoundingClientRect`) and it is the
  // frame the operator actually sees.
  useLayoutEffect(() => {
    setBottomOffset(measureBottomBar());
    setTopOffset(measureTopBar());
  }, [pathname, open, isDesktop]);

  // Re-measured on every route change as well as on resize: a page with
  // a bottom bar and one without are two different floors, and the dock
  // has to land on whichever it is standing on now.
  useEffect(() => {
    let observer = null;
    const measure = () => {
      setBottomOffset(measureBottomBar());
      setTopOffset(measureTopBar());
      // ⚠️ WATCH THE NAVBAR, DO NOT JUST MEASURE IT ONCE. It is 97px at
      // 1440 and it GROWS — a wrapped nav row, a banner, a longer
      // facility name — after this effect's frame and without a resize
      // event. A one-shot measurement leaves the dock's head (the place
      // chip and the collapse toggle) back under a bar that layers above
      // it, which is the bug this measurement exists to fix, arriving a
      // second late instead of immediately.
      if (!observer || typeof ResizeObserver !== 'function') return;
      observer.disconnect();
      topBarNodes().forEach((node) => observer.observe(node));
    };
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(() => {
        setTopOffset(measureTopBar());
      });
    }
    // After paint, so a bar that mounts with the route is already there.
    const raf = window.requestAnimationFrame
      ? window.requestAnimationFrame(measure) : setTimeout(measure, 0);
    window.addEventListener('resize', measure);
    // A STICKY bar is only at the top of the viewport once the page has
    // scrolled to it; before that it sits at its natural offset and the
    // geometry filter in `topBarNodes` correctly reports nothing. So the
    // top offset is re-read on scroll as well — passive, and it only
    // ever sets state when the number really changed (the setter is a
    // no-op for an equal value, and `measureTopBar` is two DOM reads).
    // …and ONLY on desktop. `topOffset` is read by the desktop panel's
    // `style` alone (`isDesktop ? { top: … } : undefined`), so on a
    // phone this is a `querySelectorAll` + `getBoundingClientRect` on
    // every scroll frame of every facility page for a number nothing
    // renders — on the one device the round is walked with.
    const onScroll = () => setTopOffset(measureTopBar());
    if (isDesktop) window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (window.cancelAnimationFrame) window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      if (isDesktop) window.removeEventListener('scroll', onScroll);
      if (observer) observer.disconnect();
    };
  }, [pathname, open, isDesktop]);

  // --- the two ways in ----------------------------------------------
  useEffect(() => {
    const onToggle = () => toggle();
    window.addEventListener(TOGGLE_EVENT, onToggle);
    return () => window.removeEventListener(TOGGLE_EVENT, onToggle);
  }, [toggle]);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '`' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      event.preventDefault();
      toggle();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [toggle]);

  // A turn handed to the dock from elsewhere in the app. The dock OPENS
  // for it: a turn answering into a collapsed panel is an answer nobody
  // sees, and "Walk me through it" is a request to be walked through it.
  useEffect(() => {
    const onAsk = (event) => {
      const detail = event?.detail || {};
      const utterance = (detail.utterance || '').trim();
      if (!utterance) return;
      setEverOpened(true);
      toggle(true);
      setPending((prev) => ({ text: utterance, seq: prev.seq + 1 }));
    };
    window.addEventListener(ASK_EVENT, onAsk);
    return () => window.removeEventListener(ASK_EVENT, onAsk);
  }, [toggle]);

  // Nothing to clear — the panel gates on the SEQ, not on the words —
  // but the frame still wants to know a queued turn actually went.
  const onUtteranceSent = useCallback(() => {}, []);

  const mounted = everOpened || !isDesktop;
  // On desktop a closed dock is GONE, not shrunk — a 380px column of
  // nothing is 380px the racks plan could have used. On a phone the
  // collapsed row is the dock, so it is always on screen.
  const hidden = isDesktop && !open;

  // ── THE SPACE THE PAGE HAS TO LEAVE ────────────────────────────────
  //
  // Measured, not assumed: the collapsed row is one height, the expanded
  // sheet another, and both change with the composer's line count.
  // ResizeObserver where it exists, and the route/open/layout deps
  // re-measure everywhere else.
  // The chat's own "start again", reached from this frame's header.
  const panelRef = useRef(null);
  useEffect(() => {
    const root = document.documentElement;
    const setVar = (px) => root.style.setProperty('--ff-dock-height', `${px}px`);

    // DESKTOP RESERVES NOTHING: the panel is a column beside the page,
    // not a bar across the bottom of it.
    if (isDesktop || hidden || !facilityId) {
      setVar(0);
      return undefined;
    }

    const node = rootRef.current;
    if (!node) { setVar(0); return undefined; }

    const measure = () => {
      const rect = node.getBoundingClientRect();
      setVar(Math.ceil(rect.height + bottomOffset));
    };
    measure();

    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isDesktop, hidden, facilityId, open, bottomOffset, pathname, mounted]);

  // ── AND THE DESKTOP PANEL RESERVES ITS WIDTH (re-sweep #6, item 4) ──
  //
  // ⚠️ "DESKTOP RESERVES NOTHING" ABOVE WAS ONLY TRUE OF THE HEIGHT. The
  // 380px right panel is `position: fixed` too, so at 1440 it painted
  // over the right-hand end of every page under it — `elementFromPoint`
  // at a Tasks Today card's REOPEN button returned the dock's assistant
  // bubble, and the operator's three clicks at that button went to the
  // chat. The page had no idea the column was there.
  //
  // Same mechanism as the height, mirrored: MEASURED (the panel is
  // 380px today and `max-width: 100vw` at a narrow desktop), published
  // on `<html>`, and back to `0px` the moment the dock closes, goes to
  // the bottom layout, or leaves a facility route. `.app-content` is
  // the consumer — the footer already has its own reserve and is a
  // sibling of `<main>`, so it needs the padding on its own account.
  useEffect(() => {
    const root = document.documentElement;
    const setVar = (px) => root.style.setProperty('--ff-dock-width', `${px}px`);
    // Design System v1 §2.3: the navbar's FroggerAI toggle wears the
    // accent fill while the desktop panel is OPEN — a state, read by CSS
    // (navbar.css), so the toggle needs no state of its own. Set and
    // cleared on exactly the condition the width reserve uses.
    if (isDesktop && !hidden && facilityId) root.setAttribute('data-frogger-dock-open', '1');
    else root.removeAttribute('data-frogger-dock-open');

    // The bottom dock spans the viewport and reserves HEIGHT instead; a
    // closed desktop panel is not on screen at all (`hidden`).
    if (!isDesktop || hidden || !facilityId) {
      setVar(0);
      return undefined;
    }

    const node = rootRef.current;
    if (!node) { setVar(0); return undefined; }

    const measure = () => {
      setVar(Math.ceil(node.getBoundingClientRect().width));
    };
    measure();

    let observer = null;
    if (typeof ResizeObserver === 'function') {
      observer = new ResizeObserver(measure);
      observer.observe(node);
    }
    window.addEventListener('resize', measure);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [isDesktop, hidden, facilityId, open, pathname, mounted]);

  // ⚠️ AND ON THE WAY OUT. The effects above clean up their listeners but
  // their LAST values would survive the dock leaving a facility route —
  // the storefront would then reserve 96px of nothing at the bottom of
  // every page, and 380px down its right-hand side, for the rest of the
  // session.
  useEffect(() => () => {
    document.documentElement.style.setProperty('--ff-dock-height', '0px');
    document.documentElement.style.setProperty('--ff-dock-width', '0px');
    document.documentElement.removeAttribute('data-frogger-dock-open');
  }, []);

  // A GLOBAL THE SIBLING'S "Walk me through it" BUTTON READS. Tasks Today
  // dispatches `froggerai:ask` into the void otherwise — an event with no
  // listener is silence, and the button would look broken rather than
  // absent. Deleted on unmount, so a page that outlives the dock does not
  // keep offering a button that can no longer be answered.
  useEffect(() => {
    if (!facilityId) return undefined;
    window.__froggerDockMounted = true;
    return () => { delete window.__froggerDockMounted; };
  }, [facilityId]);

  const chip = useMemo(() => placeLabel(place), [place]);
  // B — the two halves. See `placeChipParts`: the NAME is what the
  // keeper is looking for and the KIND is context, and the kind is
  // absent whenever the name already carries it.
  const chipParts = useMemo(() => placeChipParts(place), [place]);

  // THE HEAD'S CONTENTS, built once and rendered in ONE of two places:
  // its own row on desktop, or inside the composer row on a phone (see
  // the block comment at the top of this section).
  const chipNode = place ? (
    <button
      type="button"
      className="fd__chip"
      onClick={clearPlace}
      title="Clear the current place"
      data-testid="frogger-dock-chip"
      // THE SCREEN READER HEARS WHAT THE EYE SEES. The visible chip
      // gained its muted context ("1A · Rack 3") in the ninth fix
      // batch; a label that still said only "At 1A" would leave the
      // one keeper who cannot see the pill without the half that says
      // WHICH rack. "At:" is forbidden as a VISIBLE prefix, not as a
      // spoken preposition — this is a sentence, and it reads as one.
      aria-label={`At ${chip}${chipParts.context ? `, ${chipParts.context}` : ''} — tap to clear`}
      data-place-name={chipParts.name}
    >
      {/* ⚠️ THE KIND IS FIRST AND MUTED; THE NAME IS BOLD AND IS THE
          ONE THAT SURVIVES A SQUEEZE. ONE TRUNCATION FOR EVERY KIND
          (item 20, F4): the START of the name is kept and the ellipsis
          is at the end (FroggerDock.css) — "QADELETE obligate tank" read
          "…ELETE obligate tank" when tanks and racks clipped on the left.
          `<bdi>` isolates the name's own direction inside the box. */}
      {chipParts.kind && (
        <span className="fd__chip-kind">{chipParts.kind}</span>
      )}
      <span className="fd__chip-text"><bdi>{chipParts.name}</bdi></span>
      {/* WHERE THAT NAME IS, muted, and only when there is a parent to
          say it with (`placeChipParts`). "1A" alone does not tell the
          keeper which rack they are standing at, and the middle dot is
          rendered WITH the context so an empty context cannot leave a
          dangling separator behind. The button's accessible name is its
          `aria-label`, so this is not read out twice. */}
      {chipParts.context && (
        <span className="fd__chip-context">{`· ${chipParts.context}`}</span>
      )}
      <span aria-hidden="true" className="fd__chip-x">×</span>
    </button>
  ) : (
    <span className="fd__chip fd__chip--empty" data-testid="frogger-dock-chip">
      <span className="fd__chip-text"><bdi>Nowhere in particular</bdi></span>
    </span>
  );

  // ⚠️ ON A PHONE, A CHIP WITH A PLACE IN IT TAKES A ROW OF ITS OWN.
  //
  // Operator, phone pass on beta 2026-09-13, looking at a LIVE voice
  // session: *"that has to take up more space on a small cell."* The
  // collapsed dock had put the chip, the caret, the composer, the mic
  // and a red Stop in ONE row on a 390px screen, and the chip — the
  // half that says which rack the keeper is standing at — was the one
  // that gave way: it rendered as "At:…", a label with its answer
  // ellipsized out of it.
  //
  // So the one-row promise is kept where it is free and spent where it
  // is not: with NO place there is nothing to read and the empty chip
  // rides in the composer row exactly as before; with a place set the
  // chip gets a full-width row ABOVE the composer, at 16px, wrapping
  // rather than truncating (`.froggerai-chiprow` in FroggerDock.css).
  // The caret stays in the composer row either way — it is a control,
  // not a label, and it is 40px wide.
  const ownChipRow = !isDesktop && Boolean(place);

  const toggleNode = (
    <button
      type="button"
      className="fd__grow"
      onClick={() => toggle()}
      aria-expanded={open}
      aria-label={open ? 'Collapse FroggerAI' : 'Expand FroggerAI'}
      data-testid="frogger-dock-toggle"
    >
      {open ? '▾' : '▴'}
    </button>
  );

  // Desktop: start below whatever the navbar MEASURES, not below what a
  // token nobody defines claims. Phone: ride above any bottom bar, plus
  // the device's own home-indicator inset.
  const style = isDesktop
    ? { top: `${dockTop(topOffset)}px` }
    : {
      bottom: bottomOffset
        ? `calc(${bottomOffset}px + env(safe-area-inset-bottom, 0px))`
        : 'env(safe-area-inset-bottom, 0px)',
    };

  if (!facilityId) return null;

  return (
    <div
      className="fd"
      data-frogger-dock="1"
      data-layout={isDesktop ? 'desktop' : 'phone'}
      data-open={open ? '1' : '0'}
      data-bottom-offset={String(bottomOffset)}
      data-top-offset={String(topOffset)}
      role="dialog"
      aria-label="FroggerAI"
      hidden={hidden}
      style={style}
      ref={rootRef}
    >
      {/* A HEAD ON A PHONE ONLY WHILE OPEN. Collapsed is ONE ROW — the
          chip and the caret ride in the composer — and the harness pins
          that ("COLLAPSED: the chip is inside the composer row", no
          separate head). Expanded, the transcript is on screen and so is
          the one control that puts it down. */}
      {(isDesktop || open) && (
        <div className="fd__head">
          {isDesktop ? chipNode : null}
          <button
            type="button"
            className="fd__newchat"
            data-testid="frogger-dock-new-conversation"
            onClick={() => panelRef.current?.newConversation()}
            title="Start a new conversation"
          >
            New conversation
          </button>
          {isDesktop ? toggleNode : null}
        </div>
      )}

      {/* A — THE LAST ANSWER, ON THE COLLAPSED DOCK. One line,
          ellipsized, with an unread dot until it is opened; tapping it
          opens the transcript, which is where the Undo lives. It is
          rendered only when there IS an answer, so a dock nobody has
          spoken to yet is still exactly one row (the S3-06.4 promise,
          and the harness still pins it at <= 64px).

          ⚠️ IT IS NOT RESTORED ACROSS A RELOAD, deliberately — unlike
          the transcript, which is. The dock is mounted ONCE in App.js
          and survives every client-side route change, which is the case
          this exists for: the navigate rule collapses the dock the
          moment the screen moves, and the read-back has to survive that
          with its Undo one tap away. A fresh page load is a fresh
          screen, and putting a row back on it for an answer from before
          the reload would cost the one-row promise permanently, for a
          line the keeper can still reach by opening the dock. */}
      {!isDesktop && !open && preview && (
        <button
          type="button"
          className={`fd__preview${unread ? ' fd__preview--unread' : ''}`}
          onClick={() => toggle(true)}
          data-testid="frogger-dock-preview"
          data-unread={unread ? '1' : '0'}
          title={preview}
          aria-label={`Last answer: ${preview}. Tap to open FroggerAI.`}
        >
          {unread && <span aria-hidden="true" className="fd__preview-dot" />}
          <span className="fd__preview-text">{preview}</span>
        </button>
      )}

      {mounted && (
        <FroggerAIChatPanel
          ref={panelRef}
          facilityId={facilityId}
          facilityName={facilityName}
          active={open || !isDesktop}
          place={place}
          placeNowhere={nowhere}
          compact
          showWelcome={false}
          // ONE ROW on a phone when there is nothing to read — the
          // chip and the caret ride in the composer row rather than in
          // a head of their own. With a PLACE set the chip moves up to
          // `chipRow` and only the caret stays here (see `ownChipRow`).
          chipRow={ownChipRow ? chipNode : null}
          chipSlot={isDesktop ? null : (
            <>
              {ownChipRow ? null : chipNode}
              {toggleNode}
            </>
          )}
          initialUtterance={pending.text}
          utteranceKey={pending.seq}
          onUtteranceSent={onUtteranceSent}
          onNavigate={onNavigate}
          onAnswer={onAnswer}
          onPreview={onPreview}
          onNewConversation={onNewConversation}
        />
      )}
    </div>
  );
}

FroggerDock.propTypes = {
  facilityId: PropTypes.string,
  facilityName: PropTypes.string,
};

FroggerDock.defaultProps = { facilityId: null, facilityName: '' };
